import * as React from 'react';
import { createChatStore, type ChatStore, type Id, type Message } from '@norkin/chat-core';
import { ChatProvider, Conversation, useThread } from '@norkin/chat-react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ChatMessageList } from '@features/chat/components/shadcn-chat/ChatMessageList';
import { ChatComposer } from '@features/chat/components/shadcn-chat/ChatComposer';
import { cn } from '@shared/utils/cn';
import { useFlowChatRunner } from './useFlowChatRunner';

export type FlowChatRunnerHandle = {
  runId?: string;
  reset: () => void;
  isRunning: boolean;
};

type FlowChatProps = {
  flowId: string;
  workspaceId?: string | null;
  onRunnerReady?: (handle: FlowChatRunnerHandle) => void;
};

type ConversationViewportProps = {
  threadId: Id;
  messages: Message[];
  containerProps: {
    ref: React.RefObject<HTMLDivElement>;
    onScroll?: React.UIEventHandler<HTMLDivElement>;
  };
  bottomAnchorProps: {
    ref: React.RefObject<HTMLDivElement>;
  };
  atBottom: boolean;
  hasScrollableContent: boolean;
  scrollToBottom: () => void;
  usesWindowScroll: boolean;
  isStreaming: boolean;
};

function ConversationViewport({
  threadId,
  messages,
  containerProps,
  bottomAnchorProps,
  atBottom,
  hasScrollableContent,
  scrollToBottom,
  usesWindowScroll,
  isStreaming,
}: ConversationViewportProps) {
  const [anchorVisible, setAnchorVisible] = React.useState(true);

  React.useEffect(() => {
    const anchor = bottomAnchorProps.ref.current;
    const container = containerProps.ref.current;
    if (!hasScrollableContent || !anchor) {
      setAnchorVisible(true);
      return;
    }
    if (typeof IntersectionObserver !== 'function') {
      setAnchorVisible(atBottom);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => setAnchorVisible(entries.some((entry) => entry.isIntersecting)),
      usesWindowScroll || !container ? undefined : { root: container },
    );
    observer.observe(anchor);
    return () => observer.disconnect();
  }, [bottomAnchorProps, containerProps, hasScrollableContent, atBottom, usesWindowScroll]);

  const showScrollButton = hasScrollableContent && !anchorVisible;

  const baseContainerProps = containerProps as typeof containerProps & { className?: string };

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        {...baseContainerProps}
        className={cn(
          "flex-1 min-h-0 overflow-y-auto overscroll-contain rounded-lg bg-card pb-28",
          baseContainerProps.className,
        )}
        role="log"
        aria-label="Flow chat transcript"
        aria-live="polite"
      >
        <ChatMessageList
          threadId={threadId}
          messages={messages}
          isStreaming={isStreaming}
          bottomAnchorProps={bottomAnchorProps}
        />
      </div>
      {showScrollButton ? (
        <Button
          type="button"
          size="icon"
          variant="secondary"
          className="absolute bottom-4 right-4 h-9 w-9 rounded-full shadow-md"
          aria-label="Scroll to latest message"
          onClick={scrollToBottom}
        >
          <ChevronDown className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
}

export function FlowChat({ flowId, workspaceId, onRunnerReady }: FlowChatProps) {
  const storeRef = React.useRef<ChatStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = createChatStore({ microBatchMs: 0 });
  }
  const store = storeRef.current;

  React.useEffect(() => () => {
    store.dispose();
  }, [store]);

  return (
    <ChatProvider store={store}>
      <FlowChatInner flowId={flowId} workspaceId={workspaceId} store={store} onRunnerReady={onRunnerReady} />
    </ChatProvider>
  );
}

type FlowChatInnerProps = {
  flowId: string;
  workspaceId?: string | null;
  store: ChatStore;
  onRunnerReady?: (handle: FlowChatRunnerHandle) => void;
};

function FlowChatInner({ flowId, workspaceId, store, onRunnerReady }: FlowChatInnerProps) {
  const threadId = React.useMemo<Id>(() => `flowchat-${flowId}` as Id, [flowId]);
  useThread(threadId);

  const { runId, isRunning, send, reset } = useFlowChatRunner({ store, threadId, flowId, workspaceId });

  React.useEffect(() => {
    onRunnerReady?.({ runId, reset, isRunning });
  }, [runId, reset, isRunning, onRunnerReady]);

  const handleSend = React.useCallback((assistantMessageId: Id) => {
    return send(assistantMessageId);
  }, [send]);

  return (
    <div className="relative flex h-full min-h-[420px] flex-col gap-4">
      <div className="flex flex-1 min-h-0 flex-col">
        <Conversation threadId={threadId} bottomThreshold={72}>
          {(ctx) => (
            <ConversationViewport
              threadId={threadId}
              messages={ctx.messages}
              containerProps={ctx.containerProps}
              bottomAnchorProps={ctx.bottomAnchorProps}
              atBottom={ctx.atBottom}
              hasScrollableContent={ctx.hasScrollableContent}
              scrollToBottom={ctx.scrollToBottom}
              usesWindowScroll={ctx.usesWindowScroll}
              isStreaming={isRunning}
            />
          )}
        </Conversation>
      </div>
      <div className="sticky bottom-0 z-10 w-full">
        <ChatComposer
          threadId={threadId}
          disabled={isRunning}
          onSend={handleSend}
          className="border-border/80 bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60"
        />
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button type="button" size="sm" variant="outline" onClick={reset} disabled={isRunning}>
          Reset session
        </Button>
        {runId ? (
          <span className="text-xs text-muted-foreground">Run {runId.slice(0, 8)}</span>
        ) : null}
      </div>
    </div>
  );
}
